import { formatTimeRemaining, getCountdownInterval } from "@breakout/ui/utils"
import { useEffect, useState } from "react"

interface CountdownResult {
	/** Formatted time remaining string (e.g., "6d 18h", "45m", "2m 30s") */
	timeRemaining: string
	/** Whether the countdown has ended */
	isEnded: boolean
	/** Raw milliseconds remaining */
	remainingMs: number
}

/**
 * Performant countdown hook that updates at appropriate intervals:
 * - > 1 hour: updates every minute
 * - < 1 hour: updates every 30 seconds
 * - < 10 minutes: updates every second (for urgency)
 *
 * @param targetMs - Target timestamp in milliseconds, or null if no countdown
 * @returns Formatted countdown and status
 */
export function useCountdown(targetMs: number | null): CountdownResult {
	const [now, setNow] = useState(Date.now())

	useEffect(() => {
		if (!targetMs) {
			return
		}

		// Calculate initial state
		const remaining = targetMs - Date.now()
		if (remaining <= 0) {
			return
		}

		// Update function
		const update = () => setNow(Date.now())

		// Initial update
		update()

		// Set up interval with adaptive timing
		let intervalId: ReturnType<typeof setInterval>

		const scheduleNextUpdate = () => {
			const interval = getCountdownInterval(targetMs)
			intervalId = setInterval(() => {
				update()
				// Check if interval should change
				const newInterval = getCountdownInterval(targetMs)
				if (newInterval !== interval) {
					clearInterval(intervalId)
					scheduleNextUpdate()
				}
			}, interval)
		}

		scheduleNextUpdate()

		return () => clearInterval(intervalId)
	}, [targetMs])

	if (!targetMs) {
		return { timeRemaining: "TBD", isEnded: false, remainingMs: 0 }
	}

	const remainingMs = Math.max(0, targetMs - now)
	const isEnded = remainingMs === 0

	return {
		timeRemaining: isEnded ? "Ended" : formatTimeRemaining(new Date(targetMs)),
		isEnded,
		remainingMs,
	}
}
